import {
  ChatSettingsModalState,
  ChatSettingsTab,
  ConnectorsGroup,
  SkillsGroup,
} from 'llm/chat-library/sidepanel/AISidepanelV2GlobalClientState';
import {assertNever} from 'util/Types';

export function getChatUrlPath(chatId: string): string {
  return `/chats/${chatId}`;
}

/**
 * Base path for the chat settings modal. The route patterns in AIRoutes are declared as literals
 * (so they stay greppable and their precedence is easy to eyeball); this constant is the single
 * source of truth for the state -> URL direction below.
 */
const CHAT_SETTINGS_MODAL_BASE = 'chats/settings';

// Returns true if `pathname` matches the chat settings modal URL (base or any sub-route).
export function isChatSettingsModalPath(pathname: string): boolean {
  const base = `/${CHAT_SETTINGS_MODAL_BASE}`;
  return pathname === base || pathname.startsWith(`${base}/`);
}

/**
 * Stable URL segment for a tab. The enum values are internal identifiers; this exhaustive switch is
 * the single place that pins down the segment strings that appear in the address bar, so they stay
 * stable independently of the enum key/value casing.
 */
export function chatSettingsTabToUrlSegment(tab: ChatSettingsTab): string {
  switch (tab) {
    case ChatSettingsTab.GENERAL:
      return 'general';
    case ChatSettingsTab.SKILLS:
      return 'skills';
    case ChatSettingsTab.AGENTS:
      return 'agents';
    case ChatSettingsTab.CONNECTORS:
      return 'connectors';
    default:
      throw new Error(`Unexpected tab for chat settings URL segment, tab: ${assertNever(tab)}`);
  }
}

/**
 * The default group each list tab lands on.
 */
const DEFAULT_SKILLS_GROUP = SkillsGroup.ALL;
const DEFAULT_CONNECTORS_GROUP = ConnectorsGroup.AVAILABLE;

/**
 * Build the URL-derived modal state for a tab from the route inputs.
 */
export function buildChatSettingsModalState(
  tab: ChatSettingsTab,
  selectedItemId: string | null,
  previous?: ChatSettingsModalState | null
): ChatSettingsModalState {
  switch (tab) {
    case ChatSettingsTab.GENERAL:
      return {tab};
    case ChatSettingsTab.SKILLS:
      return {
        tab,
        selectedItemId,
        group: previous?.tab === ChatSettingsTab.SKILLS ? previous.group : DEFAULT_SKILLS_GROUP,
      };
    case ChatSettingsTab.AGENTS:
      return {tab, selectedItemId};
    case ChatSettingsTab.CONNECTORS:
      return {
        tab,
        selectedItemId,
        group: previous?.tab === ChatSettingsTab.CONNECTORS ? previous.group : DEFAULT_CONNECTORS_GROUP,
      };
    default:
      throw new Error(`Unexpected tab for ChatSettingsModalState, tab: ${assertNever(tab)}`);
  }
}

/**
 * Build the URL for the chat settings modal from its state. Includes a leading slash so the address
 * bar updates correctly when passed to `navigateToURL` (matching every other caller); the
 * RouteBundle patterns themselves are registered without a leading slash, per Backbone.
 */
export function chatSettingsModalUrl(
  tab: ChatSettingsTab,
  options: {selectedItemId?: string | null} = {}
): string {
  const {selectedItemId} = options;
  let url = `/${CHAT_SETTINGS_MODAL_BASE}/${chatSettingsTabToUrlSegment(tab)}`;
  if (selectedItemId != null) {
    url += `/${encodeURIComponent(selectedItemId)}`;
  }
  return url;
}
